import { Skeleton } from "@resume-studio/ui";
import { cn } from "@resume-studio/utils";
import { Activity } from "lucide-react";
import * as React from "react";
import { Helmet } from "react-helmet-async";

import { useAdminAnalytics } from "@/admin/services";

import { EmptyState } from "../_components/empty-state";
import { PageHeader } from "../_components/page-header";

const grafanaBaseUrl =
  import.meta.env.VITE_PUBLIC_GRAFANA_URL?.replace(/\/$/, "") ??
  "https://analytics.resumestudio.io";

const resolveGrafanaUrl = (rawUrl: string) => {
  try {
    return new URL(rawUrl).toString();
  } catch {
    // Keep older/stale API responses working. During the standalone admin
    // migration some clients saw `bus-pulse?kiosk...` instead of the full
    // `https://analytics.../d/bus-pulse?...` URL.
    const [pathPart, queryPart] = rawUrl.replace(/^\//, "").split("?");
    const grafanaPath = pathPart.startsWith("d/") ? `/${pathPart}` : `/d/${pathPart}`;

    return `${grafanaBaseUrl}${grafanaPath}${queryPart ? `?${queryPart}` : ""}`;
  }
};

export const AdminAnalyticsPage = () => {
  const { data, isLoading } = useAdminAnalytics();
  const [activeUid, setActiveUid] = React.useState<string | null>(null);
  const [mountedUids, setMountedUids] = React.useState<Set<string>>(() => new Set());

  React.useEffect(() => {
    if (!activeUid && data?.dashboards?.length) {
      setActiveUid(data.dashboards[0].uid);
    }
  }, [activeUid, data]);

  React.useEffect(() => {
    if (!activeUid) return;

    setMountedUids((current) => {
      if (current.has(activeUid)) return current;

      const next = new Set(current);
      next.add(activeUid);
      return next;
    });
  }, [activeUid]);

  return (
    <>
      <Helmet>
        <title>Analytics · Admin</title>
      </Helmet>

      <PageHeader
        title="Analytics"
        description="Live Grafana dashboards. Read-only embeds; signed every two minutes."
        trail={["Admin"]}
      />

      {isLoading ? (
        <Skeleton className="h-[640px] w-full" />
      ) : !data?.configured ? (
        <EmptyState
          icon={Activity}
          title="Grafana isn't configured yet"
          description="Set GRAFANA_URL and GRAFANA_SERVICE_TOKEN on the server to enable embeds. Until then the Overview/Revenue pages still work standalone."
        />
      ) : (data?.dashboards?.length ?? 0) === 0 ? (
        <EmptyState
          icon={Activity}
          title="No dashboards published"
          description="Publish a dashboard from your Grafana instance to see it here."
        />
      ) : (
        <div className="flex flex-col gap-3">
          <nav className="flex flex-wrap items-center gap-1 rounded-md border border-border bg-card p-1">
            {data?.dashboards.map((d) => (
              <button
                key={d.uid}
                onClick={() => setActiveUid(d.uid)}
                className={cn(
                  "rounded-sm px-2.5 py-1 text-[12px] font-medium transition-colors duration-120 ease-out",
                  d.uid === activeUid
                    ? "bg-surface text-foreground"
                    : "text-muted-foreground hover:bg-surface-muted hover:text-foreground",
                )}
              >
                {d.title}
              </button>
            ))}
          </nav>

          <div className="relative h-[70vh] overflow-hidden rounded-md border border-border bg-card sm:h-[80vh]">
            {data?.dashboards
              .filter((dashboard) => mountedUids.has(dashboard.uid))
              .map((dashboard) => {
                const isActive = dashboard.uid === activeUid;

                return (
                  <iframe
                    key={dashboard.uid}
                    src={resolveGrafanaUrl(dashboard.url)}
                    title={dashboard.title}
                    aria-hidden={!isActive}
                    // Hide inactive dashboards from focus order. iframes are
                    // interactive surfaces (Grafana intercepts keyboard input
                    // inside the embed) so the lint rule's "only interactive
                    // elements" check is overly conservative here.
                    // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex
                    tabIndex={isActive ? 0 : -1}
                    className={`absolute inset-0 size-full transition-opacity ${
                      isActive ? "opacity-100" : "pointer-events-none invisible opacity-0"
                    }`}
                    // Grafana iframes need same-origin to handle cookies; they don't
                    // need permissions. Keep `referrerPolicy` strict so we don't leak
                    // admin URLs to Grafana logs.
                    referrerPolicy="no-referrer"
                  />
                );
              })}
          </div>
        </div>
      )}
    </>
  );
};

export default AdminAnalyticsPage;
